import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

var WARNING_MS = 55 * 60 * 1000;  // 55 minutes
var LOGOUT_MS = 60 * 60 * 1000;   // 60 minutes
var ACTIVITY_KEY = 'rts_last_activity';
var TOKEN_CHECK_MS = 5 * 60 * 1000; // Check token every 5 min

export function useInactivityLogout() {
  var [showWarning, setShowWarning] = useState(false);
  var timerRef = useRef(null);
  var warningRef = useRef(null);
  var navigate = useNavigate();

  var logout = useCallback(function(reason) {
    // Log the event
    try {
      var email = localStorage.getItem('admin_email') || 'unknown';
      console.log('AUTO_LOGOUT', { user: email, reason: reason, timestamp: new Date().toISOString() });
    } catch (e) {}

    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem(ACTIVITY_KEY);
    sessionStorage.clear();
    setShowWarning(false);
    navigate('/admin', { state: { message: reason === 'TOKEN_EXPIRED' ? 'Session expired. Please log in again.' : 'Session expired due to inactivity. Please log in again.' } });
  }, [navigate]);

  var resetTimers = useCallback(function() {
    localStorage.setItem(ACTIVITY_KEY, Date.now().toString());
    setShowWarning(false);

    if (warningRef.current) clearTimeout(warningRef.current);
    if (timerRef.current) clearTimeout(timerRef.current);

    warningRef.current = setTimeout(function() {
      setShowWarning(true);
    }, WARNING_MS);

    timerRef.current = setTimeout(function() {
      logout('INACTIVITY');
    }, LOGOUT_MS);
  }, [logout]);

  var stayLoggedIn = useCallback(function() {
    resetTimers();
  }, [resetTimers]);

  useEffect(function() {
    if (!localStorage.getItem('admin_token')) return;

    // Activity listeners
    var events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    var throttled = false;
    function onActivity() {
      if (throttled) return;
      throttled = true;
      setTimeout(function() { throttled = false; }, 5000); // Throttle to every 5s
      resetTimers();
    }

    events.forEach(function(ev) { window.addEventListener(ev, onActivity, { passive: true }); });

    // Multi-tab sync
    function onStorage(e) {
      if (e.key === ACTIVITY_KEY && e.newValue) {
        resetTimers(); // Another tab was active
      }
    }
    window.addEventListener('storage', onStorage);

    // Token expiry check
    var tokenInterval = setInterval(function() {
      var token = localStorage.getItem('admin_token');
      if (!token) { logout('TOKEN_EXPIRED'); return; }
      try {
        var payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          logout('TOKEN_EXPIRED');
        }
      } catch (e) {
        logout('TOKEN_EXPIRED');
      }
    }, TOKEN_CHECK_MS);

    // Start timers
    resetTimers();

    return function() {
      events.forEach(function(ev) { window.removeEventListener(ev, onActivity); });
      window.removeEventListener('storage', onStorage);
      clearInterval(tokenInterval);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [resetTimers, logout]);

  return { showWarning: showWarning, stayLoggedIn: stayLoggedIn, logout: logout };
}
